import { useState } from 'react'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { Icon } from '../ui/Icon'
import { PixelPet } from '../identity/PixelPet'
import { ProjectAvatar } from '../identity/ProjectAvatar'
import { SidebarSection } from '../sidebar/SidebarSection'
import { useSections } from '../sidebar/useSections'
import { BookmarkRows } from '../agents/bookmarks/BookmarkPanel'
import { bookmarksQuery, isOldEdge } from '../agents/bookmarks/useBookmarks'
import { shownFile } from '../agents/bookmarks/jump'
import { isOldService, roomBookmarksQuery, toPanelRow, type RoomBookmark } from '../chat/useRoomBookmarks'
import { setRoomBookmark } from '../chatv1/transport'
import { deleteBookmark, type AgentRow, type Bookmark, type ProjectRow } from '../transport/api'
import { StagePage, type StageChrome } from './StagePage'
import '../agents/bookmarks/bookmarks.css'

/**
 * EVERY BOOKMARK, IN ONE PLACE — owner, 2026-11-01: 在 side panel 底下 more 的那一個欄位點進去之後呢, 我們要在頁面再新增
 * Bookmark, 也就是所有使用者他在 Agent 裡面收集到的 Bookmark, 都可以有一個集中的地方來管理.
 *
 * Each agent's bookmarks are kept on its own machine (agents/bookmarks), each project's by the chat service, the
 * viewer's alone (chat/useRoomBookmarks); this reads them all — the same queries the drawers read, so a bookmark
 * kept or removed in one place is so in the other. A group per agent and per project, the one kept in most recently
 * first. A row goes to its message there; × removes it.
 */
export type BookmarkGroup =
  | { kind: 'agent'; key: string; agent: AgentRow; list: Bookmark[]; newest: number }
  | { kind: 'room'; key: string; room: ProjectRow; list: Bookmark[]; newest: number }

/**
 * All of them, from every agent of the roster and every project room (not a group's: those keep none). `enabled: false`
 * reads only what is already known — More's button counts without asking every machine.
 */
export function useAllBookmarks(agents: AgentRow[], rooms: ProjectRow[], enabled = true) {
  const agentQs = useQueries({ queries: agents.map((a) => ({ ...bookmarksQuery(a), enabled })) })
  const roomQs = useQueries({ queries: rooms.map((r) => ({ ...roomBookmarksQuery(r.project_id), enabled })) })
  const groups: BookmarkGroup[] = []
  const unreachable: AgentRow[] = []
  agents.forEach((agent, i) => {
    const q = agentQs[i]!
    if (q.error && !isOldEdge(q.error)) unreachable.push(agent)
    const list = q.data ?? []
    if (list.length) {
      const key = `a:${agent.device_id ?? ''}:${agent.host ?? ''}:${agent.session}`
      groups.push({ kind: 'agent', key, agent, list, newest: Math.max(...list.map((b) => b.created_ts)) })
    }
  })
  rooms.forEach((room, i) => {
    const q = roomQs[i]!
    const rows: RoomBookmark[] = q.error && isOldService(q.error) ? [] : (q.data ?? [])
    if (rows.length) {
      const list = rows.map((b) => toPanelRow(room.project_id, b))
      groups.push({ kind: 'room', key: `p:${room.project_id}`, room, list, newest: Math.max(...list.map((b) => b.created_ts)) })
    }
  })
  groups.sort((a, b) => b.newest - a.newest)
  const qs = [...agentQs, ...roomQs]
  return {
    groups,
    count: groups.reduce((n, g) => n + g.list.length, 0),
    /** Every agent and room has answered (or said it keeps none): the count is the whole of it. */
    complete: qs.every((q) => q.data !== undefined || q.isError),
    loading: qs.some((q) => q.isLoading),
    unreachable,
  }
}

export function BookmarksPage({
  agents,
  rooms,
  onGoAgent,
  onGoRoom,
  ...chrome
}: StageChrome & {
  agents: AgentRow[]
  /** The project rooms — a group's room keeps no bookmarks. */
  rooms: ProjectRow[]
  onGoAgent: (agent: AgentRow, b: Bookmark) => void
  onGoRoom: (room: ProjectRow, b: Bookmark) => void
}) {
  const qc = useQueryClient()
  const all = useAllBookmarks(agents, rooms)
  const { isOpen, toggle } = useSections()
  const [said, setSaid] = useState<string | null>(null)

  /** Taken off the list at once, put back if the machine or the service refuses. */
  const remove = async (g: BookmarkGroup, id: string) => {
    setSaid(null)
    const key = g.kind === 'agent' ? bookmarksQuery(g.agent).queryKey : roomBookmarksQuery(g.room.project_id).queryKey
    const before = qc.getQueryData(key)
    if (g.kind === 'agent') qc.setQueryData<Bookmark[]>(key, (l) => (l ?? []).filter((b) => b.id !== id))
    else qc.setQueryData<RoomBookmark[]>(key, (l) => (l ?? []).filter((b) => String(b.seq) !== id))
    try {
      if (g.kind === 'agent') await deleteBookmark(id, g.agent.device_id ?? undefined)
      else await setRoomBookmark(g.room.project_id, Number(id), false)
    } catch {
      qc.setQueryData(key, before)
      setSaid(g.kind === 'agent' ? `${g.agent.session}’s machine did not remove it. Try again in a moment.` : 'That bookmark could not be removed. Try again in a moment.')
    }
    void qc.invalidateQueries({ queryKey: key })
  }

  return (
    <StagePage
      {...chrome}
      title="Bookmarks"
      backLabel="Back"
      sub={all.count ? `${all.count} kept — only you see them` : 'Only you see them'}
      glyph={
        <span className="lp-chip lp-yellow" aria-hidden="true">
          <Icon name="bookmarks" size={14} />
        </span>
      }
      className="more-page bm-page"
    >
      {all.loading && !all.groups.length && <p className="more-note">Reading your bookmarks…</p>}
      {!all.loading && !all.groups.length && (
        <p className="more-note">
          No bookmarks yet. Tap the bookmark under a message — in an agent’s conversation or in a project — and it is kept
          here.
        </p>
      )}
      {all.groups.map((g) => (
        <SidebarSection
          key={g.key}
          id={`more-bm:${g.key}`}
          label={g.kind === 'agent' ? g.agent.session : (g.room.name ?? 'Project')}
          sub={g.kind === 'agent' ? (g.agent.device_name ?? undefined) : 'project'}
          glyph={
            g.kind === 'agent' ? (
              <PixelPet name={g.agent.session} seed={g.agent.hash} size={20} />
            ) : (
              <ProjectAvatar projectId={g.room.project_id} label={g.room.name ?? g.room.project_id} size={20} />
            )
          }
          count={g.list.length}
          isOpen={isOpen(`more-bm:${g.key}`)}
          onToggle={toggle}
        >
          <BookmarkRows
            list={g.list}
            currentFile={g.kind === 'agent' ? shownFile(g.agent.device_id ?? null, g.agent.session) : g.room.project_id}
            label={`Bookmarks of ${g.kind === 'agent' ? g.agent.session : (g.room.name ?? 'the project')}`}
            onGo={(b) => (g.kind === 'agent' ? onGoAgent(g.agent, b) : onGoRoom(g.room, b))}
            onRemove={(id) => void remove(g, id)}
          />
        </SidebarSection>
      ))}
      {said && <p className="more-note err-line">{said}</p>}
      {all.unreachable.length > 0 && (
        <p className="more-note more-quiet">
          Not reachable right now, so their bookmarks are not listed:{' '}
          {all.unreachable.map((a) => `${a.session}${a.device_name ? ` on ${a.device_name}` : ''}`).join(', ')}.
        </p>
      )}
    </StagePage>
  )
}
